import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listListingPlatforms } from "@/services/channelCenter";
import { ListingCenter } from "./ListingCenter";
import { MobileSiteConsole } from "./MobileSiteConsole";
import { PartnerPreviewPanel, PartnerSiteTab } from "./PartnerSiteTab";
import { PartnerSiteConsole } from "./PartnerSiteConsole";

type TabKey = "platform" | "mobile" | "partner" | "social";

/**
 * LISTCTR-1 · 上架中心（独立模块 · 左侧菜单「商务类」· 路由 /admin/listing）。
 * 契约：designs/mockups/channel-center/上架中心-样稿.html（tag listing-center-mockup-v1）。
 * 「平台上架」= 原渠道中心的上架表原样搬过来；「手机站」= 手机站预览台（LISTCTR-2：真手机站预览 + 首页横幅 / 精选 / 线路展示清单）。
 * 平台开闸、凭据、同步日志仍在「渠道中心」（只给老板 / 技术管理员）。
 */
export function ListingCenterPage() {
  // PPR-3 · 页签 / 定位的产品放在地址里：产品详情上架状态条的每一格可点，直接落到对应页签并定位到这条（?tab=mobile|partner|platform&product=<产品 id>）
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get("tab");
  let tab: TabKey = rawTab === "mobile" || rawTab === "partner" ? rawTab : "platform";
  const extraTabs: { key: TabKey; label: string }[] = [];
  let extraTab: ReactNode = null;
  const focusProduct = sp.get("product");
  const setTab = (t: TabKey) => setSp({ tab: t }, { replace: true });
  const platformsQ = useQuery({ queryKey: ["listing-center", "platforms"], queryFn: listListingPlatforms });
  const platforms = platformsQ.data ?? [];

  return (
    <div className="flex flex-col gap-1 p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-bold text-slate-900">
          上架中心
        </h1>
        <span className="text-[13px] text-slate-500">
          选哪些产品上哪个平台、对接编码、票种、平台侧挂接 · 运营可进（按权限页勾选）· 平台开闸和凭据仍在「渠道中心」
        </span>
      </header>

      <div className="mt-4 flex gap-1 border-b border-slate-200">
        {([
          { key: "platform" as const, label: "平台上架" },
          { key: "mobile" as const, label: "手机站" },
          { key: "partner" as const, label: "同行站" },   // PPR-3 展示清单 + 以同行身份预览；PPR-6 促销页横幅 / 入口
          ...extraTabs,
        ] as { key: TabKey; label: string }[]).map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`listing-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-4 py-2.5 text-[14px] font-medium transition ${
              tab === t.key ? "border-blue-600 font-semibold text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
            {t.key === "partner" && <span className="ml-1.5 rounded bg-violet-600 px-1.5 py-0.5 text-[10.5px] font-bold text-white">新</span>}
          </button>
        ))}
      </div>

      {extraTab ?? (tab === "platform" ? (
        platformsQ.isError ? (
          <button
            type="button"
            onClick={() => platformsQ.refetch()}
            className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100"
          >
            加载失败，点击重试
          </button>
        ) : platformsQ.isLoading ? (
          <div className="mt-5 rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">加载中...</div>
        ) : (
          <ListingCenter platforms={platforms} />
        )
      ) : tab === "mobile" ? (
        <MobileSiteConsole focusProductId={focusProduct} />   /* LISTCTR-2 · 手机站预览台 + PPR-3 手机站展示清单（编辑展示） */
      ) : (
        <>
          {/* PPR-3 A · 同行站展示清单 + 以同行身份预览（放在 PPR-6 促销页横幅 / 入口上面） */}
          <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
            <PartnerPreviewPanel />
            <PartnerSiteTab focusProductId={focusProduct} />
          </div>
          <PartnerSiteConsole />   {/* PPR-6 · 促销页横幅 / 入口 */}
        </>
      ))}
    </div>
  );
}
